import { css } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';

import CheckboxAlignButtonEnum from '../../enums/CheckboxAlignButtonEnum.js';
import CheckboxLayoutEnum from '../../enums/CheckboxLayoutEnum.js';
import type ICheckboxStyleOptions from '../../types/ICheckboxStyleOptions.js';

const INPUT_SIZE = {
	horizontal: 10,
	vertical: 44
};

export default class CheckboxStyle {
	/**
	 * Returns style.
	 *
	 * @param options Options.
	 * @param options.isDisabled Disabled.
	 * @param options.isChecked Checked.
	 * @param options.theme Theme.
	 * @param options.alignButton Label position.
	 * @param options.layout Layout.
	 * @returns Styles.
	 */
	public static getStyle(options: ICheckboxStyleOptions): string {
		const { color, border, spacing, focusOutline } = options.theme;

		return css`
			${FontStyle.getStyle({
				type: ThemeFontStyleTypeEnum.bodyMedium,
				theme: options.theme
			})}

			:host {
				display: inline-block;
			}

			label {
				cursor: ${options.isDisabled ? 'default' : 'pointer'};
			}
			input[type='checkbox'] {
				position: absolute;
				top: -${INPUT_SIZE.horizontal / 16}rem;
				width: ${INPUT_SIZE.vertical / 16}rem;
				height: ${INPUT_SIZE.vertical / 16}rem;
				display: block;
				opacity: 0;
				outline: 0;
				margin: 0;
				padding: 0;
				cursor: ${options.isDisabled ? 'default' : 'pointer'};
				pointer-events: all;
				-webkit-appearance: none;
			}
			.container {
				display: flex;
				position: relative;
				align-items: flex-start;
				flex-direction: ${options.alignButton === CheckboxAlignButtonEnum.left
					? 'row'
					: 'row-reverse'};
			}
			.container label {
				margin-top: ${2 / 16}rem;
			}
			.checkbox {
				display: flex;
				flex-shrink: 0;
				align-items: center;
				justify-content: center;
				position: relative;
				width: ${spacing.space150 / 16}rem;
				height: ${spacing.space150 / 16}rem;
				background: ${color.neutral.white};
				border-color: ${options.invalid ? color.component.dangerDefault : color.neutral.grey500};
				border-width: ${1 / 16}rem;
				border-radius: ${spacing.space25 / 16}rem;
				border-style: solid;
				print-color-adjust: exact;
				-webkit-print-color-adjust: exact;
			}
			.container:focus-within .checkbox,
			.container .checkbox {
				background-color: ${options.isChecked ? color.accent.primaryAccent : 'initial'};
				border-color: ${options.isChecked ? color.accent.primaryAccent : color.neutral.grey500};
				border-radius: ${border.radius.radius4 / 16}rem;
				print-color-adjust: exact;
				-webkit-print-color-adjust: exact;
			}
			.outline {
				position: absolute;
				left: 0;
				top: 0;
				right: 0;
				bottom: 0;
				z-index: inherit;
				border-radius: ${focusOutline.borderRadius / 16}rem;
				transition-property: top, left, right, bottom, opacity;
				transition-duration: ${focusOutline.transitionTime}s;
				opacity: ${options.showOutline ? 1 : 0};
			}
			kompis-icon {
				color: ${options.isChecked ? color.neutral.white : color.transparency.default};
				flex-shrink: 0;
			}
			.labelDivider {
				display: flex;
				flex-shrink: 0;
				width: ${spacing.space100 / 16}rem;
			}
			label kompis-text {
				color: ${color.neutral.grey700};
			}

			${this.getFocusStyle(options)}
			${!options.isDisabled && !options.isChecked ? this.getHoverStyle(options) : ''}
			${options.layout === CheckboxLayoutEnum.subtle ? this.getSubtleStyle(options) : ''}
			${this.getInvalidStyle(options)}
			${options.isDisabled ? this.getDisabled(options) : ''}
			${this.checkHover(options)}
		`;
	}

	/**
	 * Returns the style for focusing the button.
	 *
	 * @param options Options.
	 * @returns Styles.
	 */
	private static getInvalidStyle(options: ICheckboxStyleOptions): string {
		if (!options.invalid) {
			return '';
		}

		const { color, spacing } = options.theme;
		return css`
			.errorMessage {
				color: ${color.neutral.grey700};
				display: flex;
				align-items: flex-start;
				justify-content: ${options.alignButton === CheckboxAlignButtonEnum.left
					? 'flex-start'
					: 'flex-end'};
				margin-top: ${spacing.space50 / 16}rem;
			}
			.errorMessage kompis-text {
				color: ${color.component.dangerDefault};
			}
			.errorDivider {
				width: ${spacing.space50 / 16}rem;
			}
			.container:focus-within .checkbox,
			.container .checkbox {
				border-color: ${!options.isChecked ? color.component.dangerDefault : ''};
			}
		`;
	}

	/**
	 * Returns the style for focusing the button.
	 *
	 * @param options Options.
	 * @returns Styles.
	 */
	private static getFocusStyle(options: ICheckboxStyleOptions): string {
		if (!options.showOutline) {
			return '';
		}

		const { focusOutline } = options.theme;
		return css`
			.outline {
				box-shadow: 0 0 0 ${focusOutline.width / 16}rem ${focusOutline.color};
				left: -${focusOutline.offset / 16}rem;
				top: -${focusOutline.offset / 16}rem;
				right: -${focusOutline.offset / 16}rem;
				bottom: -${focusOutline.offset / 16}rem;
			}
		`;
	}

	/**
	 * Style for button disabled.
	 *
	 * @param options Options.
	 * @param options.theme Theme.
	 * @param options.disabled ...
	 * @returns Style for button disabled.
	 */
	private static getDisabled(options: ICheckboxStyleOptions): string {
		const { color } = options.theme;
		return css`
			label kompis-text {
				color: ${color.disabled.default};
			}
			.container:focus-within .checkbox,
			.container .checkbox {
				background-color: ${options.isChecked ? color.disabled.default : color.background.default};
				border-color: ${color.disabled.default};
			}
			kompis-icon {
				color: ${options.isChecked ? color.neutral.grey500 : color.background.default};
			}
		`;
	}

	/**
	 * Returns css rules for hoverStyle.
	 *
	 * @param options Options.
	 * @param options.theme Theme.
	 * @returns Css cssrule.
	 */
	private static getHoverStyle(options: ICheckboxStyleOptions): string {
		const { color } = options.theme;
		return css`
			@media not all and (pointer: coarse) {
				.container:hover .checkbox {
					border-color: ${color.neutral.grey900};
				}
				.container:hover kompis-icon {
					color: ${color.neutral.grey200};
				}
			}
		`;
	}

	/**
	 * Returns css rules for Subtle layout Style.
	 *
	 * @param options Options.
	 * @param options.theme Theme.
	 * @returns Styles.
	 */
	private static getSubtleStyle(options: ICheckboxStyleOptions): string {
		const { color } = options.theme;
		return css`
			.container:focus-within .checkbox,
			.container .checkbox {
				background-color: ${options.isChecked ? color.neutral.grey900 : 'initial'};
				border-color: ${options.isChecked ? color.neutral.grey900 : color.neutral.grey500};
			}
		`;
	}

	private static checkHover(options: ICheckboxStyleOptions): string {
		const { color } = options.theme;
		if (options.hover && !options.isChecked) {
			return css`
				@media not all and (pointer: coarse) {
					.container .checkbox {
						border-color: ${color.neutral.grey900};
					}
					.container kompis-icon {
						color: ${color.neutral.grey200};
					}
				}
			`;
		}
		return '';
	}
}
